import { useEffect, useMemo, useState } from 'react'
import { Plus, Rows3, Tag, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NodeRow } from '@/features/backtest/components/nodes/node-row'
import { ParamControl } from '@/features/backtest/components/nodes/param-control'
import { getParameterValue } from '@/features/backtest/components/nodes/universal-node-helpers'
import type { ParameterDefinition } from '@/features/backtest/schemas/universal-node-schema'
import { countFilterLeaves, type QueryFilterGroup } from '@/services/api/scanner-types'
import { readUniverseParams } from '@/features/backtest/utils/universe-node-state'
import {
  suggestUniverseName,
  suggestUniverseNameFromFilters,
} from '@/features/backtest/utils/suggest-universe-name'
import { relativeTime } from '@/features/backtest/components/run/run-formatters'
import {
  filterConditionRows,
  withoutConditionRow,
} from '@/features/sliced-form/utils/filter-summary'
import { HISTORY_COLUMN_MAP } from '@/features/ticker-stats/schemas/history-groups'

/**
 * The universe node's face (Chris's design, 2026-10-06): NAME over its
 * full-width field, then CONDITIONS, each enabled condition its own row in
 * the field's style (a status dot, the condition as History words it, an x
 * that removes it), then "+ Add Condition", which opens the config sheet on
 * the right, as does clicking a row. Then PREVIEW (Chris, 2026-10-28):
 * the Symbol and Target date the chart previews, moved here from Price Data,
 * with the pick-a-ticker-day button at the heading's right. Every block is a
 * standard face section (same gaps and divider as Price Data). The outputs follow; no explainer.
 *
 * Mounted through the schema's custom-section seam (`ui.customSections`),
 * after the parameters; the schema hides every generic parameter row so this
 * block is the whole face, and hands over the Preview rows' definitions
 * (`preview`). The name and the Preview fields write through
 * `data.onParameterChange`; removing a condition goes through
 * `data.onApplyUniverse`, the same apply as the sheet's, so the universe
 * resolves again.
 *
 * A blank name never has to be typed (Chris, 2026-11-01): the sheet fills it
 * from the filter preset or the conditions, a removal that leaves conditions
 * fills a blank one, and the tag button beside NAME replaces the name with the
 * conditions' suggestion on demand. A name already there is never overwritten
 * except by that button.
 */
export function UniverseConditionsSection({
  data,
  preview,
}: {
  data: Record<string, unknown>
  preview?: readonly ParameterDefinition[]
}) {
  const value = readUniverseParams(data.parameters)
  const onConfigure =
    typeof data.onConfigure === 'function' ? (data.onConfigure as () => void) : null
  const onParameterChange =
    typeof data.onParameterChange === 'function'
      ? (data.onParameterChange as (key: string, value: string) => void)
      : null
  const onPickTickerDay =
    typeof data.onPickTickerDay === 'function' ? (data.onPickTickerDay as () => void) : null
  const onApplyUniverse =
    typeof data.onApplyUniverse === 'function'
      ? (data.onApplyUniverse as (next: {
          universeName: string
          filters: QueryFilterGroup | null
        }) => void)
      : null
  const rows = useMemo(
    () => filterConditionRows(value.filters, HISTORY_COLUMN_MAP),
    [value.filters],
  )
  const suggestedName = useMemo(() => suggestUniverseName({ rows }), [rows])
  const conditionCount = value.filters ? countFilterLeaves(value.filters) : 0
  const resolvedAgo = relativeTime(value.resolvedAt)

  // The name commits on blur or Enter, not per keystroke: every parameter
  // write is its own undo step and a canvas re-render.
  const [nameDraft, setNameDraft] = useState(value.universeName)
  useEffect(() => setNameDraft(value.universeName), [value.universeName])
  const commitName = () => {
    const next = nameDraft.trim()
    if (onParameterChange && next !== value.universeName) onParameterChange('universeName', next)
  }

  const nameFromConditions = () => {
    if (!onParameterChange || !suggestedName) return
    setNameDraft(suggestedName)
    if (suggestedName !== value.universeName) onParameterChange('universeName', suggestedName)
  }

  // Apply writes the filters first and the count when the resolve returns
  // (Chris 2026-09-04); until then "0 ticker days" would be a lie.
  const summary =
    conditionCount === 0
      ? 'No conditions yet'
      : !value.resolvedAt
        ? `${conditionCount} ${conditionCount === 1 ? 'condition' : 'conditions'} · not resolved yet`
        : `${conditionCount} ${conditionCount === 1 ? 'condition' : 'conditions'} · ${value.inputCount.toLocaleString('en-US')} ticker ${value.inputCount === 1 ? 'day' : 'days'}`
  // When it last resolved rides in the summary's tooltip: on the face it ran
  // past the card's width.
  const summaryTitle = resolvedAgo ? `${summary} · resolved ${resolvedAgo} ago` : summary

  const pickTickerDay = onPickTickerDay && (
    <Button
      variant="ghost"
      size="sm"
      aria-label="Pick a ticker day"
      title={
        conditionCount > 0
          ? 'Pick a ticker day from this Stock Selection'
          : 'Add a condition to pick a day'
      }
      disabled={conditionCount === 0}
      className="universe-face__pick nodrag nopan"
      onClick={(event) => {
        event.stopPropagation()
        onPickTickerDay()
      }}
    >
      <Rows3 className="h-3.5 w-3.5" aria-hidden="true" />
    </Button>
  )

  // Each block is a standard face section (`node-section node-divider-b`), so
  // the gaps and the line between them are the ones Price Data draws.
  return (
    <div className="universe-face nodrag" data-testid="universe-conditions">
      <div data-node-section="name" className="node-section node-divider-b">
        <div className="universe-face__heading">
          <span className="node-section__heading">Name</span>
          {onParameterChange && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Name from conditions"
              title="Name from conditions"
              disabled={!suggestedName}
              className="universe-face__pick nodrag nopan"
              onClick={(event) => {
                event.stopPropagation()
                nameFromConditions()
              }}
            >
              <Tag className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
        </div>
        <Input
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
          placeholder="Name this stock selection"
          aria-label="Universe name"
          className="universe-face__field"
          data-testid="universe-name-input"
        />
      </div>

      <div data-node-section="conditions" className="node-section node-divider-b">
        <div className="universe-face__heading">
          <span className="node-section__heading">Conditions</span>
          <span className="universe-face__summary" title={summaryTitle}>
            {summary}
          </span>
        </div>
        {rows.map((row, index) => (
          <div
            key={`${index}-${row.display}`}
            className="universe-condition"
            data-testid="universe-condition-row"
            data-or-group={row.isOrGroup ? 'true' : undefined}
            title={row.isOrGroup ? 'Any of these' : row.display}
            {...(onConfigure
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation()
                    onConfigure()
                  },
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onConfigure()
                    }
                  },
                }
              : {})}
          >
            <span className="universe-condition__dot" aria-hidden="true" />
            <span className="universe-condition__label">{row.display}</span>
            {onApplyUniverse && (
              <button
                type="button"
                className="universe-condition__remove"
                aria-label={`Remove ${row.display}`}
                onClick={(e) => {
                  e.stopPropagation()
                  const filters = withoutConditionRow(value.filters, index, HISTORY_COLUMN_MAP)
                  onApplyUniverse({
                    universeName: value.universeName || suggestUniverseNameFromFilters(filters),
                    filters,
                  })
                }}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        {onConfigure && (
          <button
            type="button"
            className="universe-face__add"
            onClick={(e) => {
              e.stopPropagation()
              onConfigure()
            }}
            data-testid="universe-add-condition"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Condition
          </button>
        )}
      </div>

      {preview && preview.length > 0 && (
        <div
          data-node-section="preview"
          data-testid="universe-preview"
          className="node-section node-divider-b"
        >
          {/* Browse this selection's ticker days instead of typing one you
              already had to know; picking sets Symbol and Target date both.
              Without a condition there is nothing to list. */}
          <div className="universe-face__heading">
            <span className="node-section__heading">Preview</span>
            {pickTickerDay}
          </div>
          {preview.map((def) => {
            const stored = getParameterValue(data.parameters, def.name)
            return (
              <NodeRow
                key={def.name}
                label={def.displayName}
                trailing={
                  <ParamControl
                    def={def}
                    value={typeof stored === 'object' ? undefined : stored}
                    onChange={(next) => onParameterChange?.(def.name, String(next))}
                    disabled={!onParameterChange}
                  />
                }
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

export default UniverseConditionsSection
